// Libraries
import React from 'react';
import dynamic from 'next/dynamic';
// Hooks
import useEventListener from '@hooks/useEventListener';
// Utils
import { isDevelopment } from '@utils/environment';
import useSpeedTestMutationSendData from './Queries/useSpeedTestMutation.sendData';
// Types
import { useQuery } from '@tanstack/react-query';
// Constants
import { SPEEDTEST_SKU_IDS, SPEEDTEST_IFRAME_URL, DEV_TEST_DATA } from './constants';
// Components
import LazyComponent from '@components/LazyComponent';
import Maybe from '@components/Maybe';

import { Button, BUTTON_THEMES, LoaderComponent } from 'ht-styleguide';
import ProductCards from './Parts/ProductCards';
import { formatSpeeds } from './helpers';
// Styles
import styles from './speedTest.module.scss';

const SpeedFaqDynamic = dynamic(() => import('./Parts/SpeedTestFaq'), { ssr: false });

type EventProps = {
  origin: string;
  data: {
    latency: {
      minimum: number;
      jitter: number;
    };
    units: number;
    upload: number;
    download: number;
  };
};
/**
 *  getRelatedSkus():
 *  When we click on the Test button ("GO" on staging and Production and "Send Fake Test Data" on development),
 *  we call skus api to get related skus
 * @returns Array of skus with ids (SPEEDTEST_SKU_IDS)
 */
const getRelatedSkus = async () => {
  const apiURL = process.env.NEXT_PUBLIC_API_HOST_FULL_URL;
  const skusResponse = await Promise.all(SPEEDTEST_SKU_IDS.map(id => fetch(`${apiURL}/api/clients/skus/${id}`).then(response => response.json())));
  return skusResponse.map(sku => sku.sku);
};

const SpeedTestPage = () => {
  /*
   * Mutation: useSpeedTestMutationSendData - we get isSuccess which determins the api to fetch skus
   * Query: gives us data; and isFetching property.
   */
  const { isSuccess, mutate } = useSpeedTestMutationSendData();
  const { data: skus, isFetching } = useQuery({ queryKey: ['speedSkus'], queryFn: getRelatedSkus, enabled: isSuccess });

  /*
   * Listens to the message event from iframe and sends results to BE using mutate.
   */
  const handleMessageEvent = (event: EventProps) => {
    const validOrigin = event.origin === SPEEDTEST_IFRAME_URL;
    const { upload, download, latency, units } = event.data;
    const isCompleteResponse = validOrigin && [upload, download, latency].every(f => f);
    if (!isCompleteResponse) return null;
    const results = {
      ping: latency.minimum,
      jitter: latency.jitter,
      upload: formatSpeeds({ type: upload, units }),
      download: formatSpeeds({ type: download, units }),
    };
    return mutate(results);
  };

  const handleLocalTestData = () => {
    // Since the test won't run on localhost, we need a way to test the test results being saved in DB
    const { ping, jitter, upload, download } = DEV_TEST_DATA;
    const results = {
      ping,
      jitter,
      upload: formatSpeeds({ type: upload, units: 'Kbps' }),
      download: formatSpeeds({ type: download, units: 'Kbps' }),
    };
    return mutate(results);
  };

  useEventListener({ eventName: 'message', handler: handleMessageEvent });

  return (
    <>
      <iframe
        title="speed test page"
        width="100%"
        frameBorder="0"
        height="640px"
        src={SPEEDTEST_IFRAME_URL}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      />
      <div className={styles.extrasContainer}>
        <Maybe condition={isDevelopment()}>
          <Button theme={BUTTON_THEMES.PRIMARY} onClick={handleLocalTestData}>
            Send Fake Test Data
          </Button>
        </Maybe>
        <Maybe condition={isFetching}>
          <div className="paddingTop-small2">
            <LoaderComponent />
          </div>
        </Maybe>
        <Maybe condition={isSuccess}>
          <ProductCards skus={skus} showCta={false} showHover />
        </Maybe>
        <LazyComponent minHeight="400px">
          <SpeedFaqDynamic />
        </LazyComponent>
      </div>
    </>
  );
};

export default React.memo(SpeedTestPage);
